"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { parseStrategyWeights } from "@/app/components/StrategyAboutContent";
import { bastionButtonIconColor, bastionButtonStyle } from "@/lib/bastion-button";
import { getStrategyIconForCode } from "@/lib/strategy-icons";
import { weightLabelField } from "@/lib/methodology/example-fields";
import type { ExampleCompany } from "@/lib/methodology/example";
import type { MethodologyStrategy } from "@/lib/methodology/strategies";
import { metricBarScoreColor } from "@/lib/stock-score-fields";
import { RATING_TIER_GREEN, RATING_TIER_RED, RATING_TIER_YELLOW } from "@/lib/rating-tiers";
import "./MethodExample.css";

const FALLBACK_COLOR = "#DCE2EC";

/** Dual-factor ratings are built from two factor ratings, in the order of their public tagline. */
const DUAL_FACTORS: Record<string, [string, string]> = {
  QualityGrowth: ["PureQuality", "PureGrowth"],
  GrowthMomentum: ["PureGrowth", "PureMomentum"],
  ValueMomentum: ["PureValue", "PureMomentum"],
};

const DUAL_PENALTY_BELOW = 60;

/**
 * Multi-factor ratings orbit the company like planets, each at its own distance (see the
 * `.mex-slot-*` rules); the single-factor ratings they are built from sit below as the foundation.
 */
const PLANET_SLOTS: Record<string, string> = {
  GEAR5FScore: "outer-left",
  QualityGrowth: "inner-left",
  GrowthMomentum: "inner-right",
  ValueMomentum: "outer-right",
};

type Row = { key: string; name: string; weight: number | null; value: number | null; scale: 10 | 100 };

function ratingColor(value: number | null): string {
  if (value == null) return "rgba(255,255,255,0.35)";
  if (value >= 67) return RATING_TIER_GREEN;
  if (value >= 34) return RATING_TIER_YELLOW;
  return RATING_TIER_RED;
}

function strategyName(strategy: MethodologyStrategy): string {
  return strategy.public_name || strategy.strategyname || strategy.strategycode;
}

function buildRows(
  strategy: MethodologyStrategy,
  company: ExampleCompany,
  singleByCode: Map<string, MethodologyStrategy>,
): { rows: Row[]; weighted: string | null; note: string | null } {
  const code = strategy.strategycode;

  const dual = DUAL_FACTORS[code];
  if (dual) {
    const rows = dual.map((factor) => ({
      key: factor,
      name: singleByCode.get(factor) ? strategyName(singleByCode.get(factor)!) : factor,
      weight: null,
      value: company.ratings[factor] ?? null,
      scale: 100 as const,
    }));
    const weak = rows.filter((row) => row.value != null && row.value < DUAL_PENALTY_BELOW);
    const note = weak.length
      ? `${weak.map((row) => row.name).join(" and ")} below ${DUAL_PENALTY_BELOW}: penalty applies`
      : `Both above ${DUAL_PENALTY_BELOW}: no penalty`;
    return { rows, weighted: null, note };
  }

  const weights = parseStrategyWeights(strategy.strategyweights);

  if (singleByCode.has(code)) {
    const rows = weights.map(({ name, weight }) => {
      const field = weightLabelField(name);
      return { key: name, name, weight, value: field ? company.scores[field] ?? null : null, scale: 10 as const };
    });
    return { rows, weighted: weightedScore(rows), note: null };
  }

  // GEAR+: weights point at the five single-factor ratings ("Pure Growth (30%)").
  const rows = weights.map(({ name, weight }) => {
    const factor = name.replace(/\s+/g, "");
    return {
      key: factor,
      name: singleByCode.get(factor) ? strategyName(singleByCode.get(factor)!) : name,
      weight,
      value: company.ratings[factor] ?? null,
      scale: 100 as const,
    };
  });
  return { rows, weighted: weightedScore(rows), note: null };
}

/** Sum of weight x value, shown in the card footer. Null when an input is missing. */
function weightedScore(rows: Row[]): string | null {
  if (rows.length === 0 || rows.some((row) => row.value == null || row.weight == null)) return null;
  const total = rows.reduce((sum, row) => sum + ((row.weight ?? 0) * (row.value ?? 0)) / 100, 0);
  return total.toFixed(1);
}

function StrategyCard({
  strategy,
  company,
  singleByCode,
  cardRef,
  active,
  onHover,
}: {
  strategy: MethodologyStrategy;
  company: ExampleCompany;
  singleByCode: Map<string, MethodologyStrategy>;
  cardRef: (node: HTMLElement | null) => void;
  active: boolean;
  onHover: (code: string | null) => void;
}) {
  const rating = company.ratings[strategy.strategycode] ?? null;
  const { rows, weighted, note } = buildRows(strategy, company, singleByCode);
  const color = strategy.color || FALLBACK_COLOR;
  const Icon = getStrategyIconForCode(strategy.strategycode, strategy.icon);

  const scale = rows[0]?.scale ?? 10;

  return (
    <article
      ref={cardRef}
      className={`mex-card ${active ? "is-active" : ""}`}
      onMouseEnter={() => onHover(strategy.strategycode)}
      onMouseLeave={() => onHover(null)}
    >
      <header className="mex-card-head">
        <h4
          className="mex-badge"
          style={{ ...bastionButtonStyle({ color, isSelected: true, height: 24, paddingX: 8 }), cursor: "default", gap: 6 }}
        >
          {Icon ? <Icon size={12} strokeWidth={2.2} color={bastionButtonIconColor(color, true)} /> : null}
          <span className="mex-badge-label">{strategyName(strategy)}</span>
        </h4>
        <p className="mex-score">
          <span className="mex-rating" style={{ color: ratingColor(rating) }}>
            {rating == null ? "–" : Math.round(rating)}
          </span>
          {rating == null ? null : (
            <span className="mex-beats">Better than {Math.min(99, Math.round(rating))}% of companies worldwide</span>
          )}
        </p>
      </header>
      <ul className="mex-rows">
        {rows.map((row) => {
          const tint = row.scale === 10 ? metricBarScoreColor(row.value) : ratingColor(row.value);
          const fill = row.value == null ? 0 : (row.value / row.scale) * 100;
          return (
            <li key={row.key} className="mex-row">
              <span className="mex-row-name">{row.name}</span>
              {row.weight == null ? null : <span className="mex-weight">{row.weight}%</span>}
              <span className="mex-value" style={{ color: row.value == null ? undefined : tint }}>
                {row.value == null ? "–" : Math.round(row.value)}
              </span>
              <span className="mex-track">
                <span className="mex-fill" style={{ width: `${fill}%`, background: tint }} />
              </span>
            </li>
          );
        })}
      </ul>
      {weighted ? (
        <p className="mex-foot">
          <span>Weighted score</span>
          <span className="mex-foot-value">
            {weighted} / {scale}
          </span>
        </p>
      ) : note ? (
        <p className="mex-foot">{note}</p>
      ) : null}
    </article>
  );
}

type Line = { key: string; code: string | null; d: string; color: string; dot: [number, number] | null };
type Orbit = { code: string; r: number; color: string };

/** Horizontal gap above the foundation row where the trunk splits. */
const BUS_OFFSET = 16;

export default function MethodExample({
  company,
  single,
  multi,
}: {
  company: ExampleCompany | null;
  single: MethodologyStrategy[];
  multi: MethodologyStrategy[];
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const sunRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef(new Map<string, HTMLElement>());
  const [lines, setLines] = useState<Line[]>([]);
  const [orbits, setOrbits] = useState<Orbit[]>([]);
  const [center, setCenter] = useState<[number, number]>([0, 0]);
  const [topHeight, setTopHeight] = useState<number | null>(null);
  const [topBottom, setTopBottom] = useState(0);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hovered, setHovered] = useState<string | null>(null);

  const measure = useCallback(() => {
    const root = rootRef.current;
    const sun = sunRef.current;
    if (!root || !sun) return;
    const box = root.getBoundingClientRect();
    setSize({ w: box.width, h: box.height });
    // Connectors only make sense in the wide layout.
    if (getComputedStyle(root).getPropertyValue("--mex-orbit").trim() !== "1") {
      setLines([]);
      setOrbits([]);
      setTopHeight(null);
      return;
    }
    const s = sun.getBoundingClientRect();
    const cx = s.left + s.width / 2 - box.left;
    const cy = s.top + s.height / 2 - box.top;
    const r = s.width / 2;
    const rect = (code: string) => {
      const card = cardRefs.current.get(code);
      if (!card) return null;
      const c = card.getBoundingClientRect();
      return { left: c.left - box.left, right: c.right - box.left, top: c.top - box.top, bottom: c.bottom - box.top };
    };
    const next: Line[] = [];

    // Planets are absolutely placed, so the orbit area takes its height from the lowest one.
    const top = topRef.current?.getBoundingClientRect();
    let lowest = 0;
    const nextOrbits: Orbit[] = [];

    // Multi-factor: a spoke to the nearest point of each card, with that card's orbit through it.
    for (const strategy of multi) {
      const c = rect(strategy.strategycode);
      if (!c) continue;
      if (top) lowest = Math.max(lowest, c.bottom + box.top - top.top);
      const x2 = Math.max(c.left, Math.min(c.right, cx));
      const y2 = Math.max(c.top + 22, Math.min(c.bottom - 22, cy));
      const angle = Math.atan2(y2 - cy, x2 - cx);
      const x1 = cx + Math.cos(angle) * r;
      const y1 = cy + Math.sin(angle) * r;
      const color = strategy.color || FALLBACK_COLOR;
      next.push({ key: strategy.strategycode, code: strategy.strategycode, d: `M${x1},${y1}L${x2},${y2}`, color, dot: [x2, y2] });
      nextOrbits.push({ code: strategy.strategycode, r: Math.hypot(x2 - cx, y2 - cy), color });
    }
    if (top) {
      setTopHeight(Math.ceil(lowest));
      setTopBottom(top.top - box.top + Math.ceil(lowest));
    }
    setOrbits(nextOrbits);
    setCenter([cx, cy]);

    // Single-factor: one trunk down from the company, split above the foundation row.
    const base = single
      .map((strategy) => ({ strategy, c: rect(strategy.strategycode) }))
      .filter((item): item is { strategy: MethodologyStrategy; c: NonNullable<ReturnType<typeof rect>> } => !!item.c);
    if (base.length > 0) {
      const busY = Math.min(...base.map(({ c }) => c.top)) - BUS_OFFSET;
      const xs = base.map(({ c }) => (c.left + c.right) / 2);
      next.push({
        key: "trunk",
        code: null,
        d: `M${cx},${cy + r}L${cx},${busY}M${Math.min(...xs)},${busY}L${Math.max(...xs)},${busY}`,
        color: "rgba(255,255,255,0.5)",
        dot: null,
      });
      base.forEach(({ strategy, c }, index) => {
        next.push({
          key: strategy.strategycode,
          code: strategy.strategycode,
          d: `M${xs[index]},${busY}L${xs[index]},${c.top}`,
          color: strategy.color || FALLBACK_COLOR,
          dot: [xs[index], c.top],
        });
      });
    }
    setLines(next);
  }, [single, multi]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(root);
    return () => observer.disconnect();
  }, [measure]);

  if (!company || (single.length === 0 && multi.length === 0)) return null;

  const singleByCode = new Map(single.map((strategy) => [strategy.strategycode, strategy]));
  const setCardRef = (code: string) => (node: HTMLElement | null) => {
    if (node) cardRefs.current.set(code, node);
    else cardRefs.current.delete(code);
  };

  const renderCard = (strategy: MethodologyStrategy) => (
    <StrategyCard
      key={strategy.strategycode}
      strategy={strategy}
      company={company}
      singleByCode={singleByCode}
      cardRef={setCardRef(strategy.strategycode)}
      active={hovered === strategy.strategycode}
      onHover={setHovered}
    />
  );

  return (
    <figure className="mex" aria-label={`${company.name}: every Bastion rating and what is inside it`}>
      <div ref={rootRef} className="mex-orbit">
        <svg className="mex-lines" width={size.w} height={size.h} aria-hidden>
          <defs>
            <clipPath id="mex-orbit-clip">
              <rect x={0} y={0} width={size.w} height={topBottom + 6} />
            </clipPath>
          </defs>
          <g clipPath="url(#mex-orbit-clip)">
            {orbits.map((orbit) => (
              <circle
                key={orbit.code}
                className={`mex-ring ${hovered === orbit.code ? "is-active" : ""}`}
                cx={center[0]}
                cy={center[1]}
                r={orbit.r}
                stroke={orbit.color}
                fill="none"
              />
            ))}
          </g>
          {lines.map((line) => (
            <g key={line.key} className={line.code && hovered === line.code ? "is-active" : undefined}>
              <path d={line.d} stroke={line.color} fill="none" />
              {line.dot ? <circle cx={line.dot[0]} cy={line.dot[1]} r={2.5} fill={line.color} /> : null}
            </g>
          ))}
        </svg>

        <div ref={topRef} className="mex-top" style={topHeight ? { height: topHeight } : undefined}>
          <div className="mex-center">
            <div ref={sunRef} className="mex-sun">
              <span className="mex-ticker">{company.ticker}</span>
              <span className="mex-name">{company.name}</span>
            </div>
          </div>
          {multi.map((strategy) => (
            <div key={strategy.strategycode} className={`mex-planet mex-slot-${PLANET_SLOTS[strategy.strategycode] ?? "extra"}`}>
              {renderCard(strategy)}
            </div>
          ))}
        </div>

        <div className="mex-base">{single.map(renderCard)}</div>
      </div>
    </figure>
  );
}
